import axios from "axios";

import { InvalidationPayload } from "../components/InvalidationModal/InvalidationModal";
import {
  CreatePickupRequest,
  PickupRequestDetails,
  PickupRequestDocument,
  PickupRequestReason,
  UpdatePickupRequest,
} from "../types";
import {
  toFormValues,
  toUpdatePickupRequest,
} from "../components/PickupRequestDialog/modules/mapper";

export interface CreatePickupRequestResponse {
  requestNumber: string;
}

export const createPickupRequest = (
  payload: CreatePickupRequest
): Promise<CreatePickupRequestResponse> =>
  axios
    .post<CreatePickupRequestResponse>("pickupRequests", payload)
    .then((res) => res.data);

export interface PickupRequestCollector {
  companyId: number;
  displayName: string;
  regNumber: number;
  registrationNumber: string | null;
  // RPRA registration number (Companies.RPRARegistrationNumber); the eTracks participant number is regNumber.
  addressLine1: string | null;
  city: string | null;
}

// The request service serves this rather than /companies/search: that endpoint is gated on
// Participants.ParticipantProfile, a different feature subtree, and its 403 resets the session.
export const getPickupRequestCollectors = (
  _key: string,
  searchText: string
): Promise<PickupRequestCollector[]> =>
  axios
    .get<PickupRequestCollector[]>("pickupRequests/collectors", {
      params: { searchText, limit: 20 },
    })
    .then((res) => res.data);

export const getPickupRequestReasons = (): Promise<PickupRequestReason[]> =>
  axios
    .get<PickupRequestReason[]>("pickupRequests/reasons")
    .then((res) => res.data);

export interface UploadPickupRequestDocumentParams {
  requestNumber: string;
  file: File;
  description?: string;
}

export const uploadPickupRequestDocument = ({
  requestNumber,
  file,
  description,
}: UploadPickupRequestDocumentParams): Promise<PickupRequestDocument> => {
  const formData = new FormData();

  formData.append("File", file);

  if (description) {
    formData.append("Description", description);
  }

  return axios
    .post<PickupRequestDocument>(
      `pickupRequests/${requestNumber}/documents`,
      formData,
      {
        // axiosConfig sets a 30s default on the shared instance; a supporting document runs to
        // 200 MB and cannot finish inside it, and a request created without its document has no
        // recovery path from this screen.
        timeout: 0,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then((res) => res.data);
};

export const updatePickupRequest = (
  requestNumber: string,
  payload: UpdatePickupRequest
): Promise<void> =>
  axios
    .put(`pickupRequests/${requestNumber}`, payload)
    .then(() => undefined);

export const invalidatePickupRequest = (
  requestNumber: string,
  payload: InvalidationPayload
): Promise<void> =>
  axios
    .post(`pickupRequests/${requestNumber}/invalidate`, payload)
    .then(() => undefined);

export const deleteDocument = (
  requestNumber: string,
  documentId: number
): Promise<void> =>
  axios
    .delete(`pickupRequests/${requestNumber}/documents/${documentId}`)
    .then(() => undefined);

export const getRequest = (
  requestNumber: string
): Promise<PickupRequestDetails> =>
  axios
    .get<PickupRequestDetails>(`pickupRequests/${requestNumber}`)
    .then((res) => res.data);

export const getDocuments = (
  requestNumber: string
): Promise<PickupRequestDocument[]> =>
  axios
    .get<PickupRequestDocument[]>(`pickupRequests/${requestNumber}/documents`)
    .then((res) => res.data);

export const getDocumentDownloadPath = (
  requestNumber: string,
  documentId: number
): string => `pickupRequests/${requestNumber}/documents/${documentId}`;

export const getDocumentBlob = (path: string): Promise<Blob> =>
  axios
    .get<Blob>(path, { responseType: "blob", timeout: 0 })
    .then((res) => res.data);

export const sendPickupRequest = (requestNumber: string): Promise<void> =>
  axios.post(`pickupRequests/${requestNumber}/send`).then(() => undefined);

export const resendPickupRequest = (requestNumber: string): Promise<void> =>
  axios.post(`pickupRequests/${requestNumber}/resend`).then(() => undefined);

export const removePickupRequestCarrier = (
  requestNumber: string
): Promise<void> =>
  axios
    .post(`pickupRequests/${requestNumber}/removeCarrier`)
    .then(() => undefined);

export const removePickupRequestHauler = (
  requestNumber: string
): Promise<void> =>
  axios
    .post(`pickupRequests/${requestNumber}/removeHauler`)
    .then(() => undefined);

export const assignPickupRequestCarrier = (
  requestNumber: string,
  carrierId: number
): Promise<void> =>
  axios
    .post(`pickupRequests/${requestNumber}/assignCarrier`, { carrierId })
    .then(() => undefined);

// The PUT replaces every field, so it is built from a fresh read: a stale copy would write
// back a concurrent edit. Null carrier is intentional - the server clears it on a hauler change.
// Null company clears the hauler, the only way to do it in InReview where removeHauler refuses.
export const replacePickupRequestHauler = async (
  requestNumber: string,
  newCompanyId: number | null
): Promise<void> => {
  const request = await getRequest(requestNumber);

  await updatePickupRequest(
    requestNumber,
    toUpdatePickupRequest(
      { ...toFormValues(request), haulerId: newCompanyId, carrierId: null },
      {
        reasonIds: request.reasonIds,
        otherReasonText: request.otherReasonText ?? "",
      }
    )
  );
};
